import React from 'react';
import ImageValidations from './ImageValidations';
import StickerPreview from './StickerPreview';
import { getPaymentMethods } from '../payments/PaymentMethods';
import { PaymentMethodInfo } from '../payments/PaymentMethodInfo';
import { Board } from '../utils/Primitives';


export type StickerContentUploaderProps = {
    handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    imagePreview: string | null;
    imageSize: number | null;
    imageContentType: string | null;
    aspectRatio: number | null;
    stickerName: string | null;
    setStickerName: (name: string) => void;
    stickerUrl: string | null;
    setStickerUrl: (url: string) => void;
    stickerDescription: string | null;
    setStickerDescription: (description: string) => void;
    boardRef: React.MutableRefObject<Board>;
    premiumPrice: number;
    setPremiumPrice: (price: number) => void;
    paymentCoin: number;
    setPaymentCoin: (coin: number) => void;
    startAddingToken: () => void;
    forceStackedHeader?: boolean;
}


export default function StickerContentUploader(props: StickerContentUploaderProps) {
    const {
        handleImageChange,
        imagePreview,
        imageSize,
        imageContentType,
        aspectRatio,
        stickerName,
        setStickerName,
        stickerUrl,
        setStickerUrl,
        stickerDescription,
        setStickerDescription,
        boardRef,
        premiumPrice,
        setPremiumPrice,
        paymentCoin,
        setPaymentCoin,
        startAddingToken,
        forceStackedHeader = false,
    } = props;

    const urlChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        const trimmedUrl = url ? url.trim() : '';
        setStickerUrl(trimmedUrl);
    }

    const paymentMethods = getPaymentMethods({ boardRef });
    const paymentMethod = paymentMethods[paymentCoin];

    const handlePremiumPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        setPremiumPrice(isNaN(value) ? 0 : value);
    }   

    const handlePaymentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        if (selectedValue === '-1') {
          startAddingToken();
        } else {
          setPaymentCoin(parseInt(selectedValue));
        }
    };

    const headerRowClasses = forceStackedHeader
        ? 'flex flex-col gap-4'
        : 'flex flex-col md:flex-row md:items-center md:justify-between gap-4';

    const uploadButtonClasses = [
        'inline-flex items-center gap-3 px-4 py-3 bg-slate-900 text-white',
        'rounded-xl shadow-sm hover:bg-slate-800 cursor-pointer transition',
        forceStackedHeader ? 'self-start w-fit' : '',
    ].join(' ').trim();

    return (
        <div className='w-full flex flex-col items-center gap-10'>
            <div className='w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8'>
                <div className={headerRowClasses}>
                    <div>
                        <h2 className='text-xl font-semibold'>Pick an image to get started</h2>
                        <p className='text-slate-600 mt-1'>PNG or JPG file 0.8 MB in size or less. Transparent background recommended. </p>
                    </div>
                    <label className={uploadButtonClasses}>
                        <span className='text-sm font-medium'>Upload image</span>
                        <input
                            id="imageInput"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className='hidden'
                        />
                    </label>
                </div>

                {imagePreview && (
                    <div className="mt-6">
                        <ImageValidations imageSize={imageSize} imageContentType={imageContentType} imageAspectRatio={aspectRatio} />
                    </div>
                )}
            </div>

            <div className='w-full text-black'>
                {imagePreview && (
                    <div className='bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8'>
                        <div className='text-center'>
                            <h2 className='text-xl font-semibold text-slate-900'>Review new sticker</h2>
                        </div>
                        <div className='mt-6'>
                            <StickerPreview imagePreview={imagePreview} stickerName={stickerName} />
                        </div>
                        <div className='flex flex-col space-y-4 mt-6'>
                            <div className="flex flex-col">
                                <span className='font-medium mx-2  mt-6  text-slate-800'>Name</span>
                                <input className='border border-slate-200 rounded-lg mt-1 px-3 py-2 bg-white text-slate-900 shadow-inner'
                                       type='text'
                                       value={stickerName ?? ''}
                                       onChange={(e) => {setStickerName(e.target.value)}} />
                            </div>

                            <div className="flex flex-col">
                                <span className='font-medium mx-2 mt-3 text-slate-800'>URL (optional)</span>
                                <input className='border border-slate-200 rounded-lg mt-1 qpx-3 py-2 bg-white text-slate-900 shadow-inner'
                                       type='url'
                                       value={`${stickerUrl || ''}`}
                                       maxLength={365}
                                       onChange={urlChangeHandler} />
                                {stickerUrl && stickerUrl.trim() !== '' && !stickerUrl.startsWith('https://') && (
                                    <span className='text-red-500 text-sm mt-1'>URL must begin with https://</span>
                                )}
                            </div>

                            <div className="flex flex-col">
                                <span className='font-medium mx-2 mt-3 text-slate-800'>Description (optional)</span>
                                <textarea className='border border-slate-200 mt-1 rounded-lg px-3 py-2 bg-white text-slate-900 shadow-inner'
                                          value={stickerDescription || ''}
                                          onChange={(e) => {setStickerDescription(e.target.value)}}
                                          maxLength={563} />
                            </div>

                            <div className="flex flex-col">
                                <label htmlFor="price" className="font-medium mx-2 mt-3 text-slate-800">Price</label>
                                <div className="flex flex-col md:flex-row md:items-center gap-2 mt-1">
                                    <input
                                        type='number'
                                        step={paymentCoin === 0 ? '0.001' : '1'}
                                        min='0'
                                        value={premiumPrice}
                                        onChange={handlePremiumPriceChange}
                                        className="border border-slate-200 rounded-lg w-full md:w-2/5 px-3 py-2 bg-white text-slate-900 shadow-inner"
                                    />
                                    <select
                                        value={paymentCoin}
                                        onChange={handlePaymentTypeChange}
                                        className='w-full md:w-3/5 px-3 py-2 bg-white text-slate-900 border border-slate-200 rounded-lg shadow-inner'
                                    >
                                        {Object.values(paymentMethods).map((method) => (
                                            method && (
                                                <option key={method.id} value={method.id}>
                                                    {`${method.symbol} (${method.name})`}
                                                </option>
                                            )
                                        ))}
                                        <option value="-1">ADD TOKEN</option>
                                    </select>
                                </div>
                                {paymentMethod && (
                                    <div className="mt-2">
                                        <PaymentMethodInfo paymentMethod={paymentMethod} />
                                    </div>
                                )}
                                <div className='italic text-xs mt-4 text-slate-600'>Note: In addition to price, sticker publisher gets half of protocol fee.</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
